// Lightweight User-Agent parser. Server currently stores the raw UA
// string; parsing it here keeps the DB schema untouched while giving
// the Sessions card a nice Device / Browser / Platform breakdown.
//
// Heuristics only — good enough for a security operator overview.

const RULES = [
    { key: "browser", re: /Edg\/(\S+)/,       name: "Edge" },
    { key: "browser", re: /OPR\/(\S+)/,       name: "Opera" },
    { key: "browser", re: /Chrome\/(\S+)/,    name: "Chrome" },
    { key: "browser", re: /Firefox\/(\S+)/,   name: "Firefox" },
    { key: "browser", re: /Version\/([^\s]+).+?Safari\//, name: "Safari" },
    { key: "browser", re: /Safari\/(\S+)/,    name: "Safari" },
];

export function parseUserAgent(uaRaw) {
    const ua = (uaRaw || "").trim();
    if (!ua) {
        return { device: "Unknown", browser: "Unknown", platform: "Unknown", isMobile: false };
    }

    // Platform / OS
    let platform = "Other";
    let device = "Desktop";
    let isMobile = false;
    if (/iPhone|iPod/.test(ua))              { platform = "iOS";     device = "iPhone";  isMobile = true; }
    else if (/iPad/.test(ua))                { platform = "iPadOS";  device = "iPad";    isMobile = true; }
    else if (/Android/.test(ua))             { platform = "Android"; device = /Mobile/.test(ua) ? "Android phone" : "Android tablet"; isMobile = true; }
    else if (/Windows NT/.test(ua))          { platform = "Windows"; device = "Desktop"; }
    else if (/Mac OS X/.test(ua))            { platform = "macOS";   device = "Desktop"; }
    else if (/CrOS/.test(ua))                { platform = "ChromeOS"; device = "Chromebook"; }
    else if (/Linux/.test(ua))               { platform = "Linux";   device = "Desktop"; }

    // Browser
    let browser = "Unknown";
    for (const r of RULES) {
        const m = ua.match(r.re);
        if (m) { browser = `${r.name} ${m[1].split(".")[0]}`; break; }
    }

    // Native app UA — DEV-012C · matches both the historical "SolarFlow/1.2"
    // stamp used by pre-Build 4 clients AND the new "EnergyFlow/x.y" or
    // "Pearl-EnergyFlow/x.y" stamps rolled out from Build 5. The user-visible
    // label is always "Pearl - EnergyFlow app".
    const nativeAppMatch = ua.match(/(?:SolarFlow|EnergyFlow|Pearl-EnergyFlow)\/(\S+)/i);
    if (nativeAppMatch) {
        browser = `Pearl - EnergyFlow app ${nativeAppMatch[1]}`;
        device = isMobile ? device : "Mobile app";
    }

    return { device, browser, platform, isMobile };
}

// Human-friendly relative time.
export function relTime(iso) {
    if (!iso) return "—";
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return iso;
    const diff = Date.now() - t;
    const s = Math.round(diff / 1000);
    if (s < 60)   return `${s}s ago`;
    const m = Math.round(s / 60);
    if (m < 60)   return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24)   return `${h}h ago`;
    const d = Math.round(h / 24);
    if (d < 30)   return `${d}d ago`;
    const mo = Math.round(d / 30);
    if (mo < 12)  return `${mo}mo ago`;
    return `${Math.round(mo / 12)}y ago`;
}

export function ageBetween(fromIso, toIso = null) {
    if (!fromIso) return "—";
    const from = new Date(fromIso).getTime();
    const to = toIso ? new Date(toIso).getTime() : Date.now();
    if (Number.isNaN(from) || Number.isNaN(to)) return "—";
    const diff = Math.max(0, to - from);
    const m = Math.round(diff / 60000);
    if (m < 60) return `${m} min`;
    const h = Math.floor(m / 60);
    const mm = m % 60;
    if (h < 24) return mm ? `${h}h ${mm}m` : `${h}h`;
    const d = Math.floor(h / 24);
    const hh = h % 24;
    return hh ? `${d}d ${hh}h` : `${d}d`;
}
